import { useMemo, useState } from 'react';
import type { EChartsOption } from 'echarts';
import { RefreshCw, Loader2, ArrowLeft } from 'lucide-react';
import { Button, ControlledTabs } from '@/components/ui';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Chart } from '@/components/ui/chart';
import { LivePageConnectionIndicator } from '@/components/ui/LivePageConnectionIndicator';
import { cn } from '@/lib/cn';
import { useTheme } from '@/store/theme-context';
import { useDotplot, useSummary, useAnalyticsCatalog } from '@/hooks/useAnalytics';
import { isReady } from '@/types/analytics';
import type {
  AnalyticsMeta,
  CatalogResponse,
  SummaryReady,
} from '@/types/analytics';
import {
  categoryColor,
  userDisplayName,
  userInitials,
} from '@/lib/analytics/dotplot';
import { DotPlotGrid } from '@/components/analytics/DotPlotGrid';
import { DotPlotSkeleton } from '@/components/analytics/DotPlotSkeleton';
import { UserDotPlot } from '@/components/analytics/UserDotPlot';

const DAY_OPTIONS = [7, 14, 30, 60, 90] as const;
const DOTPLOT_LIMIT = 200;

export default function OrgAnalyticsPage() {
  const [days, setDays] = useState<number>(14);
  const [tab, setTab] = useState<'activity' | 'overview'>('activity');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const { resolvedTheme } = useTheme();

  const catalogQuery = useAnalyticsCatalog();
  const { query: dotplotQuery, forceRefresh: refreshDotplot } = useDotplot({
    days,
    limit: DOTPLOT_LIMIT,
  });
  const { query: summaryQuery, forceRefresh: refreshSummary } = useSummary({ days });

  const catalog = catalogQuery.data;
  const dotplot = dotplotQuery.data;
  const summary = summaryQuery.data;

  const dotplotReady = isReady(dotplot) ? dotplot : undefined;
  const summaryReady = isReady(summary) ? summary : undefined;

  // Resolves to undefined if the selected user drops out of the returned set
  // (e.g. after a day-range change), which cleanly falls back to the list.
  const selectedUser = selectedUserId
    ? dotplotReady?.users.find((u) => u.userId === selectedUserId)
    : undefined;

  const refreshing =
    dotplotQuery.isFetching || summaryQuery.isFetching;

  const onRefresh = () => {
    refreshDotplot();
    refreshSummary();
  };

  return (
    <div className="space-y-6">
      {/* Header + filter row */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Analytics</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Per-user activity and usage across your organization. Admin-only.
            </p>
          </div>
          <LivePageConnectionIndicator className="mt-1.5" />
        </div>
        <div className="flex items-center gap-3">
          <SegmentedDays value={days} onChange={setDays} />
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={cn('mr-2 h-4 w-4', refreshing && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      <StaleBanner
        meta={dotplotReady?.meta ?? summaryReady?.meta}
        pending={!isReady(dotplot) || !isReady(summary)}
        lastError={pendingError(dotplot) ?? pendingError(summary)}
      />

      <ControlledTabs
        tabs={[
          { id: 'activity', label: 'User activity' },
          { id: 'overview', label: 'Overview' },
        ]}
        activeTab={tab}
        onChange={(id) => setTab(id as 'activity' | 'overview')}
      >
        {tab === 'activity' ? (
          /* Dot plot — hero */
          <section className="space-y-3">
        {selectedUser && catalog && dotplotReady ? (
          /* Single-user focus view — categories × days */
          <>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedUserId(null)}
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> All users
              </Button>
              <div className="flex items-center gap-2">
                {selectedUser.avatarUrl ? (
                  <img
                    src={selectedUser.avatarUrl}
                    alt=""
                    className="h-7 w-7 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
                    {userInitials(selectedUser)}
                  </span>
                )}
                <div>
                  <h2 className="text-lg font-semibold leading-tight text-foreground">
                    {userDisplayName(selectedUser)}
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Activity across all types
                    {selectedUser.firstActiveDay &&
                      ` · first active ${selectedUser.firstActiveDay}`}
                  </p>
                </div>
              </div>
            </div>

            <UserDotPlot
              user={selectedUser}
              days={dotplotReady.days}
              catalog={catalog}
            />

            <OnboardingLegend />
          </>
        ) : (
          /* Multi-user split view — one grid per activity type */
          <>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                User activity by type
              </h2>
              {dotplotReady?.meta.truncated && (
                <p className="text-xs text-muted-foreground">
                  Showing{' '}
                  {dotplotReady.meta.returnedUsers ?? dotplotReady.users.length} of{' '}
                  {dotplotReady.meta.totalUsers} users (top by activity).
                </p>
              )}
            </div>

            {!dotplotReady || !catalog ? (
              <DotPlotSkeleton />
            ) : dotplotReady.users.length === 0 ? (
              <EmptyState />
            ) : (
              <DotPlotGrid
                days={dotplotReady.days}
                users={dotplotReady.users}
                catalog={catalog}
                onSelectUser={setSelectedUserId}
              />
            )}

            {dotplotReady && catalog && dotplotReady.users.length > 0 && (
              <OnboardingLegend />
            )}
          </>
        )}
          </section>
        ) : (
          <div className="space-y-6">
            {/* KPI tiles */}
            <KpiRow
              summary={summaryReady}
              catalog={catalog}
              totalUsers={dotplotReady?.meta.totalUsers}
              loading={!summaryReady}
            />

            {/* Breakdowns */}
            <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <ChartCard title="Daily active users">
          {summaryReady ? (
            <Chart option={dailyActiveOption(summaryReady)} height={260} />
          ) : (
            <Skeleton className="h-[260px] w-full" />
          )}
        </ChartCard>
        <ChartCard title="Category mix">
          {summaryReady && catalog ? (
            <Chart
              option={categoryMixOption(summaryReady, catalog, resolvedTheme)}
              height={260}
            />
          ) : (
            <Skeleton className="h-[260px] w-full" />
          )}
        </ChartCard>
        <ChartCard title="Runs by trigger × runtime">
          {summaryReady ? (
            <Chart option={runsOption(summaryReady)} height={260} />
          ) : (
            <Skeleton className="h-[260px] w-full" />
          )}
        </ChartCard>
              <ChartCard title="Top tool groups">
                {summaryReady ? (
                  <Chart option={toolGroupsOption(summaryReady)} height={260} />
                ) : (
                  <Skeleton className="h-[260px] w-full" />
                )}
              </ChartCard>
            </section>
          </div>
        )}
      </ControlledTabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

function pendingError(
  body: { status: string; lastError?: string } | undefined,
): string | undefined {
  return body && body.status === 'pending' ? body.lastError : undefined;
}

function SegmentedDays({
  value,
  onChange,
}: {
  value: number;
  onChange: (d: number) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-border">
      {DAY_OPTIONS.map((d) => (
        <button
          key={d}
          type="button"
          onClick={() => onChange(d)}
          className={cn(
            'px-3 py-1.5 text-sm transition-colors',
            d !== DAY_OPTIONS[0] && 'border-l border-border',
            value === d
              ? 'bg-primary text-primary-foreground'
              : 'bg-card text-muted-foreground hover:bg-muted',
          )}
        >
          {d}d
        </button>
      ))}
    </div>
  );
}

function StaleBanner({
  meta,
  pending,
  lastError,
}: {
  meta: AnalyticsMeta | undefined;
  pending: boolean;
  lastError?: string;
}) {
  if (pending) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Computing report… this can take up to ~40 s on a cold cache.
        {lastError && (
          <span className="text-amber-600 dark:text-amber-500">
            Last attempt failed ({lastError}); retrying.
          </span>
        )}
      </div>
    );
  }
  if (!meta) return null;
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span>Updated {formatTimestamp(meta.computedAt)}</span>
      {meta.stale && (
        <span className="inline-flex items-center gap-1 text-primary">
          <Loader2 className="h-3 w-3 animate-spin" /> updating…
        </span>
      )}
    </div>
  );
}

function KpiRow({
  summary,
  catalog,
  totalUsers,
  loading,
}: {
  summary: SummaryReady | undefined;
  catalog: CatalogResponse | undefined;
  totalUsers: number | undefined;
  loading: boolean;
}) {
  const tiles = useMemo(() => {
    if (!summary) return [];
    const lastDaily = summary.daily[summary.daily.length - 1];
    const totalEvents = summary.daily.reduce((s, d) => s + d.events, 0);
    const totalRuns = summary.runs.reduce((s, r) => s + r.count, 0);
    const runsByRuntime = new Map<string, number>();
    for (const r of summary.runs) {
      runsByRuntime.set(r.agentRuntime, (runsByRuntime.get(r.agentRuntime) ?? 0) + r.count);
    }
    const runtimeSub = [...runsByRuntime.entries()]
      .map(([rt, n]) => `${n} ${rt}`)
      .join(' · ');
    const firstSeenEvent = catalog?.markers.firstSeen;
    const newUsers = firstSeenEvent
      ? summary.byEvent.find((e) => e.event === firstSeenEvent)?.count ?? 0
      : undefined;

    const messagesByEntry = new Map<string, number>();
    for (const m of summary.messages ?? []) {
      messagesByEntry.set(m.entry, (messagesByEntry.get(m.entry) ?? 0) + m.count);
    }
    const totalMessages = [...messagesByEntry.values()].reduce((s, n) => s + n, 0);
    const humanMsgs = messagesByEntry.get('human') ?? 0;
    const automationMsgs = messagesByEntry.get('automation') ?? 0;

    return [
      {
        title: 'Active users',
        value: lastDaily ? lastDaily.activeUsers : 0,
        sub: totalUsers != null ? `${totalUsers} in window` : 'today',
      },
      { title: 'Events', value: totalEvents, sub: 'total in window' },
      { title: 'Runs', value: totalRuns, sub: runtimeSub || 'in window' },
      {
        title: 'New users',
        value: newUsers ?? '—',
        sub: 'first seen',
      },
      {
        title: 'Messages',
        value: totalMessages,
        sub: `${humanMsgs.toLocaleString()} human · ${automationMsgs.toLocaleString()} automated`,
      },
    ];
  }, [summary, catalog, totalUsers]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="mt-3 h-8 w-16" />
              <Skeleton className="mt-2 h-3 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      {tiles.map((t) => (
        <Card key={t.title}>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">{t.title}</p>
            <p className="mt-1 text-3xl font-semibold text-foreground">
              {typeof t.value === 'number' ? t.value.toLocaleString() : t.value}
            </p>
            <p className="mt-1 truncate text-xs text-muted-foreground">{t.sub}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ChartCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="mb-2 text-sm font-semibold text-foreground">{title}</h3>
        {children}
      </CardContent>
    </Card>
  );
}

function EmptyState() {
  return (
    <div className="rounded-lg border border-dashed border-border bg-card p-10 text-center">
      <p className="text-sm font-medium text-foreground">No activity yet</p>
      <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
        Metrics accumulate from deploy time forward — there's no retroactive
        history. As people use the platform, their activity will appear here.
      </p>
    </div>
  );
}

function OnboardingLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1.5">
        <span className="h-3 w-3.5 rounded-sm bg-amber-400/30" />
        Onboarding day (tinted cell)
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full border-[1.5px] border-muted-foreground" />
        Automation only
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ECharts option builders (summary breakdowns)
// ---------------------------------------------------------------------------

function dailyActiveOption(summary: SummaryReady): EChartsOption {
  return {
    tooltip: { trigger: 'axis' },
    xAxis: { type: 'category', data: summary.daily.map((d) => d.day) },
    yAxis: { type: 'value' },
    series: [
      {
        name: 'Active users',
        type: 'line',
        smooth: true,
        showSymbol: false,
        areaStyle: { opacity: 0.12 },
        data: summary.daily.map((d) => d.activeUsers),
      },
    ],
  };
}

function categoryMixOption(
  summary: SummaryReady,
  catalog: CatalogResponse,
  theme: 'light' | 'dark',
): EChartsOption {
  const countByCat = new Map(summary.byCategory.map((c) => [c.category, c]));
  const cats = catalog.categories.filter((c) => (countByCat.get(c.key)?.count ?? 0) > 0);
  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    xAxis: { type: 'category', data: cats.map((c) => c.label) },
    yAxis: { type: 'value' },
    series: [
      {
        type: 'bar',
        data: cats.map((c) => ({
          value: countByCat.get(c.key)?.count ?? 0,
          itemStyle: { color: categoryColor(c, theme) },
        })),
      },
    ],
  };
}

function runsOption(summary: SummaryReady): EChartsOption {
  const triggers = [...new Set(summary.runs.map((r) => r.triggerSource))];
  const runtimes = [...new Set(summary.runs.map((r) => r.agentRuntime))];
  const lookup = new Map(
    summary.runs.map((r) => [`${r.triggerSource}|${r.agentRuntime}`, r.count]),
  );
  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { top: 0 },
    xAxis: { type: 'category', data: triggers },
    yAxis: { type: 'value' },
    series: runtimes.map((rt) => ({
      name: rt,
      type: 'bar',
      stack: 'runtime',
      data: triggers.map((t) => lookup.get(`${t}|${rt}`) ?? 0),
    })),
  };
}

function toolGroupsOption(summary: SummaryReady): EChartsOption {
  const top = [...summary.toolGroups].sort((a, b) => b.count - a.count).slice(0, 10).reverse();
  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: 12, right: 24, top: 12, bottom: 12, containLabel: true },
    xAxis: { type: 'value' },
    yAxis: { type: 'category', data: top.map((t) => t.toolGroup) },
    series: [
      {
        type: 'bar',
        data: top.map((t) => t.count),
      },
    ],
  };
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
